import { Box, CollapsibleCard, MaterialSymbol, useForm } from 'vopay';
import type { SubscriptionItem } from 'vopay';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateReportFormData, CreateReportRequest } from './types';
import {
  buildCreateReportDetailsFields,
  buildCreateReportFrequencyFields,
  buildCreateReportConfigFields,
} from './buildCreateReportsFields.helpers';

import { FieldData } from '@/components/Form/types';

type CreateReportProps = {
  subscriptions?: SubscriptionItem[];
  onSubmit: (val: CreateReportRequest, fn: () => void) => void;
  defaultValues: CreateReportFormData;
  disabledFields: (keyof CreateReportRequest)[];
  onValidate: (isValid: boolean) => void;
};

const ReportCreationForm: React.FC<CreateReportProps> = ({
  subscriptions,
  onSubmit,
  defaultValues,
  disabledFields,
  onValidate,
}: CreateReportProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Reports.SideDrawer' });
  const detailsRows = buildCreateReportDetailsFields(subscriptions);
  const frequencyRows = buildCreateReportFrequencyFields(disabledFields);
  const configRows = buildCreateReportConfigFields();
  const fieldDataList: FieldData<CreateReportFormData>[] = [...detailsRows, ...frequencyRows, ...configRows];

  const { renderFormElements, ctx } = useForm<CreateReportFormData, CreateReportRequest>({
    config: fieldDataList,
    initialValues: defaultValues,
    enableReinitialize: true,
    onSubmit: (serializedData, { resetForm }) => {
      onSubmit(serializedData, resetForm);
    },
  });

  useEffect(() => {
    onValidate(ctx.isValid);
  }, [ctx.isValid]);

  const collapsibleCardPadding = { '--collapsibleCardPadding': '24px' };

  const { AccountTransactionReportType } = ctx.values;
  const {
    RecipientEmails,
    ReportName,
    Description,
    DateRangeOption,
    Frequency,
    PaymentMethods,
    TransactionReportFailureReasons,
  } = ctx.errors;

  const isDetailsValid = !RecipientEmails && !ReportName && !Description;
  const isFrequencyValid = !DateRangeOption && !Frequency;

  const isSummaryOrPaymentRail =
    ['AccountTransactionHistoryReport', 'AccountTransactionSummary', 'TransactionsPerPaymentRail'].includes(
      AccountTransactionReportType,
    ) && !PaymentMethods;

  const isFailureDetailValid =
    AccountTransactionReportType === 'TransactionFailureDetailReport' && !TransactionReportFailureReasons;

  const isOtherTypeValid =
    AccountTransactionReportType &&
    // TODO: whitelist instead of blacklist
    ![
      'AccountTransactionHistoryReport',
      'AccountTransactionSummary',
      'TransactionsPerPaymentRail',
      'TransactionFailureDetailReport',
    ].includes(AccountTransactionReportType);

  const isConfigValid = isSummaryOrPaymentRail || isFailureDetailValid || isOtherTypeValid;

  return (
    <form id="save-subscription" onSubmit={ctx.handleSubmit}>
      <Box flx="col" base="8" gap="lg">
        <CollapsibleCard defaultOpen={true}>
          <CollapsibleCard.Trigger indicator="chevron" style={collapsibleCardPadding}>
            <Box gap="sm" ai="center">
              {isDetailsValid && <MaterialSymbol icon="check_circle" size="24px" fillAmount={1} color="#348338" />}
              <span>{t('SubTitles.details')}</span>
            </Box>
          </CollapsibleCard.Trigger>

          <CollapsibleCard.Content style={collapsibleCardPadding}>
            <Box flx="col" gap="common">
              {renderFormElements({ config: detailsRows })}
            </Box>
          </CollapsibleCard.Content>
        </CollapsibleCard>
        <CollapsibleCard>
          <CollapsibleCard.Trigger indicator="chevron" style={collapsibleCardPadding}>
            <Box gap="sm" ai="center">
              {isFrequencyValid && <MaterialSymbol icon="check_circle" size="24px" fillAmount={1} color="#348338" />}
              <span>{t('SubTitles.frequency')}</span>
            </Box>
          </CollapsibleCard.Trigger>

          <CollapsibleCard.Content style={collapsibleCardPadding}>
            <Box flx="col" gap="common">
              {renderFormElements({ config: frequencyRows })}
            </Box>
          </CollapsibleCard.Content>
        </CollapsibleCard>
        <CollapsibleCard>
          <CollapsibleCard.Trigger indicator="chevron" style={collapsibleCardPadding}>
            <Box gap="sm" ai="center">
              {isConfigValid && <MaterialSymbol icon="check_circle" size="24px" fillAmount={1} color="#348338" />}
              <span>{t('SubTitles.configuration')}</span>
            </Box>
          </CollapsibleCard.Trigger>

          <CollapsibleCard.Content style={collapsibleCardPadding}>
            <Box flx="col" gap="common">
              {renderFormElements({ config: configRows })}
            </Box>
          </CollapsibleCard.Content>
        </CollapsibleCard>
      </Box>
    </form>
  );
};

export default ReportCreationForm;
